import React from 'react';
import { Link } from 'react-router-dom';
import { Typography, Tooltip } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';

/**
 * Props for the EntityLink component.
 */
interface EntityLinkProps {
  /** The core target ID or value used to construct the final URL path */
  value: string | number;
  /** The visible text label. If omitted, the 'value' prop will be displayed */
  label?: string;
  /** The base URL path for the navigation target destination (e.g., '/tenant/products') */
  basePath: string;
  /** Optional custom title for the hover tooltip. Fallback: automatic label preview */
  tooltipTitle?: string;
  /** When true, renders a small external link/arrow icon next to the link text */
  showIcon?: boolean;
  /** Maximum maximum text width constraint before CSS truncation triggers (...) */
  maxTextWidth?: string | number;
  /** When true, forwards the entire active data row or entity state payload to the router history state */
  passStateData?: any;
  /** Optional custom key-value pairs to extend or override properties in the history navigation state */
  customState?: Record<string, any>;
}

/**
 * A highly reusable, general-purpose hyperlink component integrated with react-router-dom.
 * Supports explicit client-side route construction, automated text truncation,
 * tooltip wrapping, and unified theme palette synchronization.
 *
 * @example
 * ```tsx
 * <EntityLink
 *   value={item.item_id}
 *   label={item.item_name}
 *   basePath="/tenant/products"
 *   maxTextWidth={180}
 * />
 * ```
 */
export const EntityLink = ({
  value,
  label,
  basePath,
  tooltipTitle,
  showIcon = false,
  maxTextWidth = '100%',
  passStateData = null,
  customState = {}
}: EntityLinkProps) => {
  const displayText = label !== undefined && label !== null && label !== '' ? label : String(value);

  if (value === undefined || value === null || value === '') {
    return null;
  }

  // Construct navigation history state payload matching the system standard
  const navigationState = {
    ...(passStateData ? { duplicateData: passStateData, isDuplicate: true } : {}),
    ...customState
  };

  // Safe client-side routing resolution
  const absoluteBasePath = basePath.startsWith('/') ? basePath : `/${basePath}`;
  const targetPath = absoluteBasePath.endsWith('/')
    ? `${absoluteBasePath}${value}`
    : `${absoluteBasePath}/${value}`;

  const defaultTooltip = tooltipTitle || `Dettaglio: ${displayText}`;

  return (
    <Tooltip title={defaultTooltip} arrow placement="top" disableInteractive>
      <Link
        to={targetPath}
        state={navigationState}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          color: '#0288d1',
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: '0.875rem',
          textDecoration: 'none',
          maxWidth: '100%',
          transition: 'color 0.15s ease-in-out'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#29456c';
          e.currentTarget.style.textDecoration = 'underline';
          e.currentTarget.style.textUnderlineOffset = '3px';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = '#2578b0';
          e.currentTarget.style.textDecoration = 'none';
        }}>
        <Typography
          variant="body2"
          component="span"
          fontWeight="inherit"
          noWrap
          sx={{ maxWidth: maxTextWidth, display: 'block' }}>
          {displayText}
        </Typography>

        {showIcon && (
          <LaunchIcon
            sx={{
              fontSize: '0.75rem',
              opacity: 0.7,
              flexShrink: 0
            }}
          />
        )}
      </Link>
    </Tooltip>
  );
};

EntityLink.displayName = 'EntityLink';
